/* global clearInterval, console, CustomFunctions, OfficeRuntime, setInterval */
/** Injected by webpack DefinePlugin: default API base URL (localhost in dev, prod in prod). */
declare const __CREDITHQ_DEFAULT_API_BASE_URL__: string | undefined
import { Logger } from "../logger"

/**
 * Sends a log message to Dynatrace.
 *
 * @param logLevel The severity of the log (e.g., "info", "warn", "error").
 * @param logContent The main content of the log message.
 * @param attributes Additional key-value pairs for the log.
 */

// @todo: remote logging
// @todo: batch https://learn.microsoft.com/en-us/office/dev/add-ins/excel/custom-functions-batching
// @todo: better Excel error handling

/**
 * Interface for CreditHQ OAuth response
 */
interface CreditHQAuthResponse {
	access_token: string
	token_type: string
	expires_in: number
}

/**
 * Checks if Office.js is initialized and document settings are available
 */
function isOfficeInitialized(): boolean {
	return !!(
		Office &&
		Office.context &&
		Office.context.document &&
		Office.context.document.settings
	)
}

/** Build-time default API base URL (dev: same-origin `https://localhost:<port>/rest-api` → webpack proxies to REST API; prod: api host). */
function getDefaultApiBaseUrl(): string {
	return typeof __CREDITHQ_DEFAULT_API_BASE_URL__ !== "undefined"
		? __CREDITHQ_DEFAULT_API_BASE_URL__
		: "https://api.credithq.ai"
}

/**
 * Gets the CreditHQ API base URL: document settings override, then build default.
 * Used by both custom functions and taskpane (lookupService).
 */
export function getApiBaseUrl(): string {
	try {
		if (!isOfficeInitialized()) return getDefaultApiBaseUrl()
		return (
			(Office.context.document.settings.get("credithq_api_url") as string) ||
			getDefaultApiBaseUrl()
		)
	} catch {
		return getDefaultApiBaseUrl()
	}
}

const CREDITHQ_ACCESS_TOKEN_KEY = "credithq_access_token"

/**
 * Gets the stored access token from the current Excel session.
 * Reads from document.settings first (taskpane runtime), then OfficeRuntime.storage
 * (custom functions runtime) as fallback so formulas work even when run in a different runtime.
 * @customfunction
 * @returns The stored access token or null if not found
 */
export async function getAccessToken(): Promise<string | null> {
	let token: string | null = null
	try {
		if (isOfficeInitialized()) {
			token = Office.context.document.settings.get("access_token") as string | null
		}
	} catch {
		// document.settings not available (e.g. in custom functions runtime)
	}
	if (token) {
		const logger = await Logger.create()
		logger.info({ message: "getAccessToken called", params: [!!token] })
		return token
	}
	// Fallback: OfficeRuntime.storage is shared between taskpane and custom functions runtime
	try {
		if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
			token = await OfficeRuntime.storage.getItem(CREDITHQ_ACCESS_TOKEN_KEY)
			return token
		}
	} catch (error) {
		console.warn("OfficeRuntime.storage getItem failed:", error)
	}
	return null
}

/**
 * Syncs token from document.settings to OfficeRuntime.storage.
 * Call this when the taskpane loads and the user is authenticated so formulas
 * (which run in a different runtime) can read the token from OfficeRuntime.storage.
 */
export function syncTokenToOfficeRuntimeStorage(): void {
	try {
		if (!isOfficeInitialized() || typeof OfficeRuntime === "undefined" || !OfficeRuntime.storage)
			return
		const token = Office.context.document.settings.get("access_token") as string | null
		if (token) {
			void OfficeRuntime.storage
				.setItem(CREDITHQ_ACCESS_TOKEN_KEY, token)
				.catch(() => {})
		}
	} catch {
		// Ignore
	}
}

export function saveAccessToken(token: string | null): void {
	const value = token || ""
	try {
		if (isOfficeInitialized()) {
			Office.context.document.settings.set("access_token", token || null)
			Office.context.document.settings.saveAsync()
		}
	} catch (error) {
		console.error("Error saving access token to document settings:", error)
	}
	// Also write to OfficeRuntime.storage so custom functions runtime can read it
	try {
		if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
			void OfficeRuntime.storage.setItem(CREDITHQ_ACCESS_TOKEN_KEY, value).catch(() => {})
		}
	} catch {
		// OfficeRuntime not available
	}
}

export function saveUserName(username: string): void {
	try {
		if (!isOfficeInitialized()) {
			console.warn("Office.js not initialized")
			return
		}
		Office.context.document.settings.set("user_name", username)
		Office.context.document.settings.saveAsync()
	} catch (error) {
		console.error("Error saving user name:", error)
	}
}

/**
 * Authenticates with CreditHQ API
 * @customfunction
 * @param username CreditHQ username
 * @param password CreditHQ password
 * @returns access token
 */
export async function credithqLogin(
	username: string,
	password: string,
): Promise<string> {
	const url = `${getApiBaseUrl()}/token`

	const headers = {
		Authorization: "Basic Og==",
		Referer: "https://api.credithq.ai/docs",
		"sec-ch-ua":
			'"Google Chrome";v="137", "Chromium";v="137", "Not/A)Brand";v="24"',
		"sec-ch-ua-mobile": "?0",
		"X-Requested-With": "XMLHttpRequest",
		"User-Agent":
			"Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36",
		Accept: "application/json, text/plain, */*",
		DNT: "1",
		"Content-Type": "application/x-www-form-urlencoded",
	}

	const formData = new URLSearchParams()
	formData.append("grant_type", "password")
	formData.append("username", username)
	formData.append("password", password)

	try {
		const response = await fetch(url, {
			method: "POST",
			headers,
			body: formData,
		})

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`)
		}

		const data: CreditHQAuthResponse = await response.json()

		if (data.access_token) {
			saveAccessToken(data.access_token)
			saveUserName(username)
			return "success"
		} else {
			throw new CustomFunctions.Error(
				CustomFunctions.ErrorCode.invalidValue,
				"Authentication failed. Please check your credentials.",
			)
		}
	} catch (error) {
		console.error("CreditHQ authentication error:", error)
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.invalidValue,
			`Authentication failed with error: ${error}. Please check your credentials.`,
		)
	}
}

/**
 * Checks if user is authenticated with CreditHQ
 * @customfunction
 * @returns Authentication status
 */
export async function isCredithqAuthenticated(): Promise<boolean> {
	const token = await getAccessToken()
	return token !== null
}

/**
 * Logs out from CreditHQ by removing the stored token
 * @customfunction
 * @returns Logout status message
 */
export function credithqLogout(): string {
	try {
		if (isOfficeInitialized()) {
			Office.context.document.settings.remove("access_token")
			Office.context.document.settings.remove("user_name")
			Office.context.document.settings.saveAsync()
		}
		if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
			void OfficeRuntime.storage.removeItem(CREDITHQ_ACCESS_TOKEN_KEY).catch(() => {})
		}
		return "Successfully logged out from CreditHQ"
	} catch (error) {
		console.error("CreditHQ logout error:", error)
		return "Logout failed"
	}
}

/**
 * Common function to get attribute values from a specified URL
 * @param url The API endpoint URL to fetch data from
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @param operationName Name of the operation for logging purposes
 * @returns The attribute value as a string
 */
async function getAttributeFromUrl(
	url: string,
	query: string = "*",
	horizonSpill: boolean = true,
	operationName: string = "attribute",
): Promise<string[][]> {
	if (!(await isCredithqAuthenticated())) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"Please authenticate with CreditHQ first",
		)
	}

	const token = await getAccessToken()
	if (!token) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"No access token available",
		)
	}

	// Add jq query parameter if provided (and not the default "*")
	if (query && query !== "*") {
		const separator = url.includes("?") ? "&" : "?"
		url = `${url}${separator}jq=${encodeURIComponent(query)}`
	}

	const headers = {
		Authorization: `Bearer ${token}`,
		accept: "application/json",
	}

	console.log(`Fetching ${operationName}:`, { url, query })

	try {
		const response = await fetch(url, {
			method: "GET",
			headers,
		})

		if (!response.ok) {
			// Create a more informative error object
			const errorInfo = {
				status: response.status,
				statusText: response.statusText,
				url: url,
				message: `HTTP ${response.status}: ${response.statusText}`,
				isNotFound: response.status === 404,
				isUnauthorized: response.status === 401,
				isForbidden: response.status === 403,
			}

			// Try to get error details from response body
			try {
				const errorBody = await response.text()
				if (errorBody) {
					errorInfo.message = errorBody
				}
			} catch (e) {
				// Ignore if we can't read the response body
			}

			throw errorInfo
		}

		const data = await response.json()
		console.log("Data:", JSON.stringify(data))

		// Data is already processed by the API if query was provided, so just format it for Excel
		if (Array.isArray(data)) {
			// Return the array as individual rows
			if (horizonSpill) {
				return Array(data.map((item: any) => trimQuotes(JSON.stringify(item))))
			} else {
				return data.map((item: any) => Array(trimQuotes(JSON.stringify(item))))
			}
		} else {
			// Single object - return as single row
			return Array(Array(JSON.stringify(data)))
		}
	} catch (error) {
		console.error(`Error getting ${operationName}:`, error)

		// Handle structured error objects
		if (error && typeof error === "object" && "status" in error) {
			const errorObj = error as any

			if (errorObj.isNotFound) {
				throw new CustomFunctions.Error(
					CustomFunctions.ErrorCode.notAvailable,
					"Identifier not found (404): The requested resource does not exist",
				)
			} else if (errorObj.isUnauthorized) {
				throw new CustomFunctions.Error(
					CustomFunctions.ErrorCode.notAvailable,
					"Unauthorized (401): Please check your authentication",
				)
			} else if (errorObj.isForbidden) {
				throw new CustomFunctions.Error(
					CustomFunctions.ErrorCode.notAvailable,
					"Forbidden (403): You don't have permission to access this resource",
				)
			} else {
				throw new CustomFunctions.Error(
					CustomFunctions.ErrorCode.notAvailable,
					`HTTP ${errorObj.status}: ${errorObj.statusText || "Unknown error"}`,
				)
			}
		}

		// Handle other types of errors
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			`Failed to get attribute: ${error}`,
		)
	}
}

/**
 * Gets an attribute value for a specified entity
 * @customfunction
 * @param identifier The entity identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The attribute value as a string
 */
export async function getEntityAttribute(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/entities/code/${identifier}`
	return getAttributeFromUrl(url, query, horizonSpill, "entity attribute")
}

/**
 * Gets an attribute value for a specified instrumenti
 * @customfunction
 * @param identifier The instrument identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The attribute value as a string
 */
export async function getInstrumentAttribute(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/instruments/${identifier}`
	return getAttributeFromUrl(url, query, horizonSpill, "instrument attribute")
}

/**
 * Gets an attribute value for a specified instrument group
 * @customfunction
 * @param identifier The instrument group identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The attribute value as a string
 */
export async function getInstrumentGroupAttribute(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/instrument_groups/${identifier}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"instrument group attribute",
	)
}

/**
 * Searches for rates by name using the CreditHQ API
 * @customfunction
 * @param name The name to search for in rates
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The search results as a string array
 */
export async function searchRates(
	name: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/marketdata/rates/search/${encodeURIComponent(name)}`
	return getAttributeFromUrl(url, query, horizonSpill, "rate search")
}

/**
 * Gets rate observations for a specific rate code using the CreditHQ API
 * @customfunction
 * @param rateCode The rate code to get observations for
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @param startDate Optional start date for filtering observations (format: YYYY-MM-DD)
 * @param endDate Optional end date for filtering observations (format: YYYY-MM-DD)
 * @returns The rate observations as a string array
 */
export async function getRateObservations(
	rateCode: string,
	query: string = "*",
	horizonSpill: boolean = true,
	startDate?: string,
	endDate?: string,
): Promise<string[][]> {
	let url = `${getApiBaseUrl()}/api/v1/marketdata/rates/code/${encodeURIComponent(rateCode)}/observations`

	// Add query parameters if dates are provided
	const params = new URLSearchParams()
	if (startDate) {
		params.append("start_date", startDate)
	}
	if (endDate) {
		params.append("end_date", endDate)
	}
	if (query && query !== "*") {
		params.append("jq", query)
	}

	if (params.toString()) {
		url += `?${params.toString()}`
	}

	return getAttributeFromUrl(url, query, horizonSpill, "rate observations")
}

/**
 * Gets trades for a specific instrument using the CreditHQ API
 * @customfunction
 * @param instrumentIdentifier The CreditHQ instrument identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @param startDate Optional start date for filtering trades (format: YYYY-MM-DD)
 * @param endDate Optional end date for filtering trades (format: YYYY-MM-DD)
 * @returns The trades as a string array
 */
export async function getTrades(
	instrumentIdentifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
	startDate?: string,
	endDate?: string,
): Promise<string[][]> {
	let url = `${getApiBaseUrl()}/api/v1/marketdata/trades/code/${encodeURIComponent(instrumentIdentifier)}`

	// Add query parameters if dates are provided
	const params = new URLSearchParams()
	if (startDate) {
		params.append("start_date", startDate)
	}
	if (endDate) {
		params.append("end_date", endDate)
	}
	if (query && query !== "*") {
		params.append("jq", query)
	}

	if (params.toString()) {
		url += `?${params.toString()}`
	}

	return getAttributeFromUrl(url, query, horizonSpill, "trades")
}

/**
 * Gets trades for a specific instrument using the CreditHQ API
 * @customfunction
 * @param instrumentIdentifier The CreditHQ instrument identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @param startDate Optional start date for filtering trades (format: YYYY-MM-DD)
 * @param endDate Optional end date for filtering trades (format: YYYY-MM-DD)
 * @returns The trades as a string array
 */
export async function getAllTrades(
	query: string = "*",
	horizonSpill: boolean = true,
	startDate?: string,
	endDate?: string,
): Promise<string[][]> {
	let url = `${getApiBaseUrl()}/api/v1/marketdata/trades/`

	// Add query parameters if dates are provided
	const params = new URLSearchParams()
	if (startDate) {
		params.append("start_date", startDate)
	}
	if (endDate) {
		params.append("end_date", endDate)
	}
	if (query && query !== "*") {
		params.append("jq", query)
	}

	if (params.toString()) {
		url += `?${params.toString()}`
	}

	return getAttributeFromUrl(url, query, horizonSpill, "trades")
}

/**
 * Gets trades by trade identifier using the CreditHQ API
 * @customfunction
 * @param tradeIdentifier The CreditHQ trade identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @param startDate Optional start date for filtering trades (format: YYYY-MM-DD)
 * @param endDate Optional end date for filtering trades (format: YYYY-MM-DD)
 * @returns The trades as a string array
 */
export async function getAllTradesByTradeIdentifier(
	tradeIdentifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
	startDate?: string,
	endDate?: string,
): Promise<string[][]> {
	let url = `${getApiBaseUrl()}/api/v1/marketdata/trades/code/${encodeURIComponent(tradeIdentifier)}`

	// Add query parameters if dates are provided
	const params = new URLSearchParams()
	if (startDate) {
		params.append("start_date", startDate)
	}
	if (endDate) {
		params.append("end_date", endDate)
	}
	if (query && query !== "*") {
		params.append("jq", query)
	}

	if (params.toString()) {
		url += `?${params.toString()}`
	}

	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"trades by trade identifier",
	)
}

/**
 * Gets quotes for a specific instrument using the CreditHQ API
 * @customfunction
 * @param instrumentIdentifier The CreditHQ instrument identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @param startDate Optional start date for filtering quotes (format: YYYY-MM-DD)
 * @param endDate Optional end date for filtering quotes (format: YYYY-MM-DD)
 * @returns The quotes as a string array
 */
export async function getQuotes(
	instrumentIdentifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
	startDate?: string,
	endDate?: string,
): Promise<string[][]> {
	let url = `${getApiBaseUrl()}/api/v1/marketdata/quotes/code/${encodeURIComponent(instrumentIdentifier)}`

	// Add query parameters if dates are provided
	const params = new URLSearchParams()
	if (startDate) {
		params.append("start_date", startDate)
	}
	if (endDate) {
		params.append("end_date", endDate)
	}
	if (query && query !== "*") {
		params.append("jq", query)
	}

	if (params.toString()) {
		url += `?${params.toString()}`
	}

	return getAttributeFromUrl(url, query, horizonSpill, "quotes")
}

/**
 * Gets all funds using the CreditHQ API
 * @customfunction
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund attribute value as a string
 */
export async function getAllFundAttribute(
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/funds/`
	return getAttributeFromUrl(url, query, horizonSpill, "All funds")
}

/**
 * Gets an attribute value for a specified fund using the CreditHQ API
 * @customfunction
 * @param identifier The fund identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund attribute value as a string
 */
export async function getFundAttribute(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/funds/code/${identifier}`
	return getAttributeFromUrl(url, query, horizonSpill, "fund attribute")
}

/**
 * Gets all funds as a table (header + rows). Optional JQ filter. Recalculates when inputs change.
 * @customfunction
 * @param jq Optional JQ expression to filter/select (e.g. ".[] | select(.creditHqInstrumentGroupIdentifier == \"GROUP-ID\")")
 * @returns Table of funds that spills into the grid; first row is headers.
 */
export async function getFunds(jq?: string): Promise<string[][]> {
	if (!(await isCredithqAuthenticated())) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"Please authenticate with CreditHQ first",
		)
	}
	const token = await getAccessToken()
	if (!token) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"No access token available",
		)
	}
	let url = `${getApiBaseUrl().replace(/\/$/, "")}/api/v1/funds/`
	if (jq != null && String(jq).trim() !== "") {
		url += "?jq=" + encodeURIComponent(String(jq).trim())
	}
	const headers = {
		Authorization: `Bearer ${token}`,
		accept: "application/json",
	}
	try {
		const response = await fetch(url, { method: "GET", headers })
		if (response.status === 404) {
			return [["(no funds)"]]
		}
		if (!response.ok) {
			throw new CustomFunctions.Error(
				CustomFunctions.ErrorCode.notAvailable,
				`Funds request failed: ${response.status} ${response.statusText}`,
			)
		}
		const data = await response.json()
		const arr = Array.isArray(data) ? data : []
		return arrayToTableForSpill(arr as Record<string, unknown>[])
	} catch (err) {
		if (err instanceof CustomFunctions.Error) throw err
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			`Failed to get funds: ${err}`,
		)
	}
}

/** CamelCase/snake_case key to Title Case label. */
function keyToLabel(key: string): string {
	const noUnderscore = key.replace(/_/g, " ")
	const spaced = noUnderscore.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
	return spaced
		.split(/\s+/)
		.map((word) => (word.length === 0 ? "" : word[0].toUpperCase() + word.slice(1).toLowerCase()))
		.join(" ")
}

/** Convert array of objects to table (header row + data rows) for spill. */
function positionsToTable(arr: Record<string, unknown>[]): string[][] {
	if (arr.length === 0) return [["(no positions)"]]
	const keys = Object.keys(arr[0] ?? {})
	const header = keys.map((k) => keyToLabel(k))
	const rows = arr.map((item) =>
		keys.map((k) => {
			const v = item[k]
			if (v === null || v === undefined) return ""
			if (typeof v === "object") return JSON.stringify(v)
			return String(v)
		}),
	)
	return [header, ...rows]
}

/**
 * Gets positions for a fund as a table (header + rows). Recalculates when inputs change.
 * @customfunction
 * @param fundId Fund identifier (e.g. cell with FUNDID-xxx)
 * @param jq Optional JQ expression to filter/select fields (e.g. ".[] | { fundId, quantity, price }")
 * @returns Table of positions that spills into the grid; first row is headers.
 */
export async function getPositions(
	fundId: string,
	jq?: string,
): Promise<string[][]> {
	if (!(await isCredithqAuthenticated())) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"Please authenticate with CreditHQ first",
		)
	}
	const token = await getAccessToken()
	if (!token) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"No access token available",
		)
	}
	const trimmedId = String(fundId ?? "").trim()
	if (!trimmedId) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.invalidValue,
			"Fund ID is required",
		)
	}
	let url = `${getApiBaseUrl().replace(/\/$/, "")}/api/v1/positions/${encodeURIComponent(trimmedId)}`
	if (jq != null && String(jq).trim() !== "") {
		url += "?jq=" + encodeURIComponent(String(jq).trim())
	}
	const headers = {
		Authorization: `Bearer ${token}`,
		accept: "application/json",
	}
	try {
		const response = await fetch(url, { method: "GET", headers })
		if (response.status === 404) {
			return [["(no positions)"]]
		}
		if (!response.ok) {
			throw new CustomFunctions.Error(
				CustomFunctions.ErrorCode.notAvailable,
				`Positions request failed: ${response.status} ${response.statusText}`,
			)
		}
		const data = await response.json()
		const arr = Array.isArray(data) ? data : []
		return positionsToTable(arr as Record<string, unknown>[])
	} catch (err) {
		if (err instanceof CustomFunctions.Error) throw err
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			`Failed to get positions: ${err}`,
		)
	}
}

/**
 * Gets positions for an instrument as a table (header + rows). Recalculates when inputs change.
 * @customfunction
 * @param instrumentId Instrument identifier (e.g. cell with CHQIID-xxx)
 * @param jq Optional JQ expression to filter/select fields
 * @returns Table of positions that spills into the grid; first row is headers.
 */
export async function getPositionsByInstrument(
	instrumentId: string,
	jq?: string,
): Promise<string[][]> {
	if (!(await isCredithqAuthenticated())) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"Please authenticate with CreditHQ first",
		)
	}
	const token = await getAccessToken()
	if (!token) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"No access token available",
		)
	}
	const trimmedId = String(instrumentId ?? "").trim()
	if (!trimmedId) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.invalidValue,
			"Instrument ID is required",
		)
	}
	let url = `${getApiBaseUrl().replace(/\/$/, "")}/api/v1/positions/instrument/${encodeURIComponent(trimmedId)}`
	if (jq != null && String(jq).trim() !== "") {
		url += "?jq=" + encodeURIComponent(String(jq).trim())
	}
	const headers = {
		Authorization: `Bearer ${token}`,
		accept: "application/json",
	}
	try {
		const response = await fetch(url, { method: "GET", headers })
		if (response.status === 404) {
			return [["(no positions)"]]
		}
		if (!response.ok) {
			throw new CustomFunctions.Error(
				CustomFunctions.ErrorCode.notAvailable,
				`Positions request failed: ${response.status} ${response.statusText}`,
			)
		}
		const data = await response.json()
		const arr = Array.isArray(data) ? data : []
		return positionsToTable(arr as Record<string, unknown>[])
	} catch (err) {
		if (err instanceof CustomFunctions.Error) throw err
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			`Failed to get positions: ${err}`,
		)
	}
}

/** Convert array of objects to table (header row + data rows) for spill. */
function arrayToTableForSpill(arr: Record<string, unknown>[]): string[][] {
	if (arr.length === 0) return [["(no data)"]]
	const keys = Object.keys(arr[0] ?? {})
	const header = keys.map((k) => keyToLabel(k))
	const rows = arr.map((item) =>
		keys.map((k) => {
			const v = item[k]
			if (v === null || v === undefined) return ""
			if (typeof v === "object") return JSON.stringify(v)
			return String(v)
		}),
	)
	return [header, ...rows]
}

/**
 * Gets fund participants for a fund as a table (header + rows). Recalculates when inputs change.
 * @customfunction
 * @param fundId Fund identifier (e.g. cell with FUNDID-xxx)
 * @returns Table of fund participants that spills into the grid; first row is headers.
 */
export async function getFundParticipants(fundId: string): Promise<string[][]> {
	if (!(await isCredithqAuthenticated())) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"Please authenticate with CreditHQ first",
		)
	}
	const token = await getAccessToken()
	if (!token) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			"No access token available",
		)
	}
	const trimmedId = String(fundId ?? "").trim()
	if (!trimmedId) {
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.invalidValue,
			"Fund ID is required",
		)
	}
	const url = `${getApiBaseUrl().replace(/\/$/, "")}/api/v1/fund_participants/code/${encodeURIComponent(trimmedId)}`
	const headers = {
		Authorization: `Bearer ${token}`,
		accept: "application/json",
	}
	try {
		const response = await fetch(url, { method: "GET", headers })
		if (response.status === 404) {
			return [["(no participants)"]]
		}
		if (!response.ok) {
			throw new CustomFunctions.Error(
				CustomFunctions.ErrorCode.notAvailable,
				`Fund participants request failed: ${response.status} ${response.statusText}`,
			)
		}
		const data = await response.json()
		const arr = Array.isArray(data) ? data : []
		return arrayToTableForSpill(arr as Record<string, unknown>[])
	} catch (err) {
		if (err instanceof CustomFunctions.Error) throw err
		throw new CustomFunctions.Error(
			CustomFunctions.ErrorCode.notAvailable,
			`Failed to get fund participants: ${err}`,
		)
	}
}

/**
 * Gets all trades for a specific fund using the CreditHQ API
 * @customfunction
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The all fund trades attribute value as a string
 */
export async function getAllFundTrades(
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_trades/`
	return getAttributeFromUrl(url, query, horizonSpill, "All fund trades")
}

/**
 * Gets an attribute value for a specified fund trade by instrument identifier using the CreditHQ API
 * @customfunction
 * @param identifier The instrument identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund trade attribute value by instrument identifier as a string
 */
export async function getFundTradeAttributeByInstrumentIdentifier(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_trades/search/${identifier}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"fund trade attribute by instrument identifier",
	)
}

/**
 * Gets all fund trades by identifier using the CreditHQ API
 * @customfunction
 * @param identifier The fund trade identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund trade attribute value by fund trade identifier as a string
 */
export async function getFundTradeAttributeByIdentifier(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_trades/code/${identifier}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"fund trade attribute by fund trade identifier",
	)
}

/**
 * Gets an attribute value for a specified fund trade by trade identifier using the CreditHQ API
 * @customfunction
 * @param identifier The trade identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund trade attribute value by trade identifier as a string
 */
export async function getFundAllocationAttributeByFundIdentifier(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_allocations/search/${identifier}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"fund allocation attribute by fund allocation identifier",
	)
}

/**
 * Gets an attribute value for a specified fund allocation by fund trade identifier using the CreditHQ API
 * @customfunction
 * @param identifier The fund trade identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund allocation attribute value by fund trade identifier as a string
 */
export async function getFundAllocationAttributeByFundTradeIdentifier(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_allocations/code/${identifier}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"fund allocation attribute by fund trade identifier",
	)
}

/**
 * Gets an attribute value for a specified fund participant by fund identifier using the CreditHQ API
 * @customfunction
 * @param identifier The instrument identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund trade attribute value by instrument identifier as a string
 */
export async function getFundParticipantAttributeByFundIdentifier(
	identifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_participants/code/${identifier}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"fund participant attribute",
	)
}

/**
 * Gets an attribute value for a specified fund pipeline by id using the CreditHQ API
 * @customfunction
 * @param id The fund pipeline id
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The fund pipeline attribute value by id as a string
 */
export async function getFundPipelineAttributeById(
	id: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_pipelines/code/${id}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"fund pipeline attribute",
	)
}

/**
 * Gets all fund pipelines using the CreditHQ API
 * @customfunction
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The all fund pipelines attribute value as a string
 */
export async function getAllFundPipelineAttribute(
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/fund_pipelines/`
	return getAttributeFromUrl(url, query, horizonSpill, "All fund pipelines")
}

/**
 * Gets all valuations using the CreditHQ API
 * @customfunction
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The all valuations attribute value as a string
 */
export async function getAllValuations(
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/marketdata/valuations/`
	return getAttributeFromUrl(url, query, horizonSpill, "All valuations")
}

/**
 * Gets valuations by instrument identifier using the CreditHQ API
 * @customfunction
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The valuations attribute value by instrument identifier as a string
 */
export async function getValuationsByInstrumentIdentifier(
	instrumentIdentifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/marketdata/valuations/instrument/${encodeURIComponent(instrumentIdentifier)}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"Valuations by instrument identifier",
	)
}

/**
 * Gets valuations by valuation identifier using the CreditHQ API
 * @customfunction
 * @param valuationIdentifier The valuation identifier
 * @param query The JQ query syntax to extract specific data fields (defaults to * as raw JSON)
 * @param horizonSpill Whether to spill the result into multiple horizontal cells (defaults to true)
 * @returns The valuations attribute value by valuation identifier as a string
 */
export async function getValuationsByValuationIdentifier(
	valuationIdentifier: string,
	query: string = "*",
	horizonSpill: boolean = true,
): Promise<string[][]> {
	const url = `${getApiBaseUrl()}/api/v1/marketdata/valuations/code/${encodeURIComponent(valuationIdentifier)}`
	return getAttributeFromUrl(
		url,
		query,
		horizonSpill,
		"Valuations by valuation identifier",
	)
}

/**
 * Safely removes double quotes from the start and end of a string if they exist
 * @param input The string to trim quotes from
 * @returns The string with outer quotes removed, or the original string if no quotes exist
 */
export function trimQuotes(input: string): string {
	if (typeof input !== "string") {
		return input
	}
	return input.replace(/^"(.*)"$/, "$1")
}

CustomFunctions.associate("GETACCESSTOKEN", getAccessToken);
CustomFunctions.associate("CREDITHQLOGIN", credithqLogin);
CustomFunctions.associate("ISCREDITHQAUTHENTICATED", isCredithqAuthenticated);
CustomFunctions.associate("CREDITHQLOGOUT", credithqLogout);
CustomFunctions.associate("GETENTITYATTRIBUTE", getEntityAttribute);
CustomFunctions.associate("GETINSTRUMENTATTRIBUTE", getInstrumentAttribute);
CustomFunctions.associate("GETINSTRUMENTGROUPATTRIBUTE", getInstrumentGroupAttribute);
CustomFunctions.associate("SEARCHRATES", searchRates);
CustomFunctions.associate("GETRATEOBSERVATIONS", getRateObservations);
CustomFunctions.associate("GETTRADES", getTrades);
CustomFunctions.associate("GETALLTRADES", getAllTrades);
CustomFunctions.associate("GETALLTRADESBYTRADEIDENTIFIER", getAllTradesByTradeIdentifier);
CustomFunctions.associate("GETQUOTES", getQuotes);
CustomFunctions.associate("GETALLFUNDATTRIBUTE", getAllFundAttribute);
CustomFunctions.associate("GETFUNDATTRIBUTE", getFundAttribute);
CustomFunctions.associate("GETFUNDS", getFunds);
CustomFunctions.associate("GETPOSITIONS", getPositions);
CustomFunctions.associate("GETPOSITIONSBYINSTRUMENT", getPositionsByInstrument);
CustomFunctions.associate("GETFUNDPARTICIPANTS", getFundParticipants);
CustomFunctions.associate("GETALLFUNDTRADES", getAllFundTrades);
CustomFunctions.associate("GETFUNDTRADEATTRIBUTEBYINSTRUMENTIDENTIFIER", getFundTradeAttributeByInstrumentIdentifier);
CustomFunctions.associate("GETFUNDTRADEATTRIBUTEBYIDENTIFIER", getFundTradeAttributeByIdentifier);
CustomFunctions.associate("GETFUNDALLOCATIONATTRIBUTEBYFUNDIDENTIFIER", getFundAllocationAttributeByFundIdentifier);
CustomFunctions.associate("GETFUNDALLOCATIONATTRIBUTEBYFUNDTRADEIDENTIFIER", getFundAllocationAttributeByFundTradeIdentifier);
CustomFunctions.associate("GETFUNDPARTICIPANTATTRIBUTEBYFUNDIDENTIFIER", getFundParticipantAttributeByFundIdentifier);
CustomFunctions.associate("GETFUNDPIPELINEATTRIBUTEBYID", getFundPipelineAttributeById);
CustomFunctions.associate("GETALLFUNDPIPELINEATTRIBUTE", getAllFundPipelineAttribute);
CustomFunctions.associate("GETALLVALUATIONS", getAllValuations);
CustomFunctions.associate("GETVALUATIONSBYINSTRUMENTIDENTIFIER", getValuationsByInstrumentIdentifier);
CustomFunctions.associate("GETVALUATIONSBYVALUATIONIDENTIFIER", getValuationsByValuationIdentifier);